import type {
  ComputedFieldComputation,
  ComputedFieldComputationType,
  ComputedFieldDefinition,
  ComputedFieldResourceType,
} from "@repo/app-types/entities/computedField";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { Size } from "@repo/ui/helpers/helpers";
import { CenteredModal } from "@repo/ui/Modal/centeredModal";
import { ModalHeader } from "@repo/ui/Modal/modalHeader";
import { type FC, useState } from "react";
import { Dialog } from "react-aria-components";
import { useUpdateComputedFieldDefinitionMutation } from "../../../api/entities/computedField.js";
import {
  ComputedFieldDefinitionForm,
  parseCommaSeparatedValues,
} from "./computedFieldDefinitionForm.js";

export interface ComputedFieldDefinitionEditDialogProps {
  computedField: ComputedFieldDefinition | undefined;
  onClose: () => void;
}

function joinValues(values: readonly string[]): string {
  return values.join(", ");
}

/**
 * Dialog shell — opens iff a `computedField` is selected, and unmounts
 * the form when it closes so all form state is naturally tied to the
 * dialog and the field being edited (no in-render re-hydration needed).
 */
export const ComputedFieldDefinitionEditDialog: FC<
  ComputedFieldDefinitionEditDialogProps
> = ({ computedField, onClose }) => {
  return (
    <CenteredModal
      isOpen={computedField !== undefined}
      onOpenChange={(isOpen) => !isOpen && onClose()}
      isDismissable
      size={Size.LARGE}
    >
      <Dialog className="px-4 pt-5 pb-4 text-left sm:p-6">
        {() => (
          <>
            <ModalHeader isCloseVisible>Edit computed field</ModalHeader>
            {computedField ? (
              <ComputedFieldDefinitionEditForm
                computedField={computedField}
                onClose={onClose}
              />
            ) : null}
          </>
        )}
      </Dialog>
    </CenteredModal>
  );
};

interface ComputedFieldDefinitionEditFormProps {
  computedField: ComputedFieldDefinition;
  onClose: () => void;
}

const ComputedFieldDefinitionEditForm: FC<
  ComputedFieldDefinitionEditFormProps
> = ({ computedField, onClose }) => {
  const [updateComputedFieldDefinition, { isLoading }] =
    useUpdateComputedFieldDefinitionMutation();

  const [resourceType, setResourceType] = useState<ComputedFieldResourceType>(
    computedField.resource_type,
  );
  const [displayName, setDisplayName] = useState(computedField.display_name);
  const [computationType, setComputationType] =
    useState<ComputedFieldComputationType>(computedField.computation.type);
  const [sourceCustomFieldId, setSourceCustomFieldId] = useState<string | null>(
    computedField.computation.source_custom_field_definition_id,
  );
  const [values, setValues] = useState(
    computedField.computation.type === "count_in"
      ? joinValues(computedField.computation.values)
      : "",
  );
  const [numeratorValues, setNumeratorValues] = useState(
    computedField.computation.type === "rate_in"
      ? joinValues(computedField.computation.numerator_values)
      : "",
  );
  const [denominatorValues, setDenominatorValues] = useState(
    computedField.computation.type === "rate_in"
      ? joinValues(computedField.computation.denominator_values)
      : "",
  );
  const [error, setError] = useState<string | null>(null);

  const computation: ComputedFieldComputation | null = (() => {
    if (!sourceCustomFieldId) return null;
    if (computationType === "count_in") {
      const parsed = parseCommaSeparatedValues(values);
      if (parsed.length === 0) return null;
      return {
        type: "count_in",
        source_custom_field_definition_id: sourceCustomFieldId,
        values: parsed,
      };
    }

    const numerator = parseCommaSeparatedValues(numeratorValues);
    const denominator = parseCommaSeparatedValues(denominatorValues);
    if (denominator.length === 0) return null;
    return {
      type: "rate_in",
      source_custom_field_definition_id: sourceCustomFieldId,
      numerator_values: numerator,
      denominator_values: denominator,
    };
  })();

  const isFormValid = displayName.trim() !== "" && computation !== null;

  const handleResourceTypeChange = (value: ComputedFieldResourceType) => {
    if (value !== resourceType) {
      // The eligible source fields depend on the resource type.
      setSourceCustomFieldId(null);
    }
    setResourceType(value);
  };

  const handleSubmit = async () => {
    if (!isFormValid || !computation) return;
    try {
      await updateComputedFieldDefinition({
        id: computedField.id,
        body: {
          display_name: displayName.trim(),
          resource_type: resourceType,
          computation,
        },
      }).unwrap();
      onClose();
    } catch (err: any) {
      console.error("Failed to update computed field definition:", err);
      if (err.status === 409) {
        setError(
          err.data?.error || "A computed field with this name already exists.",
        );
      } else if (err.status === 400) {
        setError(err.data?.error || "Invalid computed field configuration.");
      } else {
        setError("Failed to update computed field. Please try again.");
      }
    }
  };

  return (
    <>
      <div className="my-4 space-y-4">
        <ComputedFieldDefinitionForm
          resourceType={resourceType}
          displayName={displayName}
          computationType={computationType}
          sourceCustomFieldDefinitionId={sourceCustomFieldId}
          values={values}
          numeratorValues={numeratorValues}
          denominatorValues={denominatorValues}
          onResourceTypeChange={handleResourceTypeChange}
          onDisplayNameChange={setDisplayName}
          onComputationTypeChange={setComputationType}
          onSourceCustomFieldDefinitionIdChange={setSourceCustomFieldId}
          onValuesChange={setValues}
          onNumeratorValuesChange={setNumeratorValues}
          onDenominatorValuesChange={setDenominatorValues}
        />

        {error && <div className="text-sm text-destructive">{error}</div>}
      </div>

      <div className="flex justify-end space-x-2">
        <Button variant={ButtonVariant.Secondary} onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant={ButtonVariant.Primary}
          onClick={handleSubmit}
          isLoading={isLoading}
          isDisabled={!isFormValid}
        >
          Save changes
        </Button>
      </div>
    </>
  );
};
